import { useEffect } from 'react';
import { ActionCenterItemV2 } from '@cloudcost/shared-types/action-center-v2';

import {
  ActionCenterViewSelection,
  useActionCenterViewContext,
} from '@/contexts/action-center-view-context';
import { ActionCenterModule } from '@/utils/actionCenterDetails';
import { useActionItemDetails } from '@/hooks/useActionItemDetails';

export interface ActionCenterViewHandlerArgs {
  item: ActionCenterItemV2;
  selection: ActionCenterViewSelection;
  clearSelection: () => void;
}

export type ActionCenterViewHandler = (args: ActionCenterViewHandlerArgs) => void;

interface UseActionCenterViewControllerOptions {
  module: ActionCenterModule;
  items?: ActionCenterItemV2[];
  handler: ActionCenterViewHandler;
  enabled?: boolean;
}

/**
 * Watches for Action Center selections for a module and invokes the supplied
 * handler once the target item data is available.
 */
export function useActionCenterViewController({
  module,
  items,
  handler,
  enabled = true,
}: UseActionCenterViewControllerOptions) {
  const { selection, clearSelection } = useActionCenterViewContext();
  const shouldResolveItem =
    enabled && selection?.module === module ? selection.itemId : undefined;
  const { item: fetchedItem } = useActionItemDetails(
    module,
    shouldResolveItem,
    items,
  );
  const resolvedItem = selection?.item ?? fetchedItem;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (!selection) {
      return;
    }
    if (selection.module !== module) {
      return;
    }
    if (!selection.itemId) {
      return;
    }
    if (!resolvedItem) {
      return;
    }

    handler({
      item: resolvedItem,
      selection,
      clearSelection,
    });
  }, [enabled, selection, module, resolvedItem, handler, clearSelection]);
}

